import { issue } from './issues.ts';
import type { Issue, Section, Step, VariableDef, VariableType, Workflow } from './types.ts';
import { VARIABLE_PATTERN } from './types.ts';

const VARIABLE_TYPES: readonly VariableType[] = ['text', 'number', 'boolean'];
const WORKFLOW_KEYS = ['variables', 'sections', 'steps'];
const SECTION_KEYS = ['id', 'title', 'steps'];
const VARIABLE_KEYS = ['type', 'unit', 'source'];

function warnUnknownKeys(raw: Record<string, unknown>, known: readonly string[], where: string, issues: Issue[]): void {
  for (const key of Object.keys(raw)) {
    if (!known.includes(key)) {
      issues.push(
        issue(
          'warning',
          'unknown_field',
          `${where} has an unknown field "${key}". It is dropped when saved.`,
          undefined,
          key
        )
      );
    }
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeVariables(raw: unknown, issues: Issue[]): Record<string, VariableDef> {
  const out: Record<string, VariableDef> = {};
  if (raw === undefined || raw === null) return out;
  if (!isRecord(raw)) {
    issues.push(issue('error', 'bad_structure', 'variables must be a mapping of names to types.'));
    return out;
  }
  for (const [name, def] of Object.entries(raw)) {
    if (!VARIABLE_PATTERN.test(name)) {
      issues.push(
        issue(
          'error',
          'bad_structure',
          `"${name}" is not a valid variable name. Use letters, digits and underscores, not starting with two underscores.`
        )
      );
      continue;
    }
    const spec = isRecord(def) ? def : { type: def };
    warnUnknownKeys(spec, VARIABLE_KEYS, `Variable "${name}"`, issues);
    const type = VARIABLE_TYPES.includes(spec['type'] as VariableType) ? (spec['type'] as VariableType) : undefined;
    if (!type)
      issues.push(issue('error', 'bad_structure', `Variable "${name}" needs a type: text, number or boolean.`));
    const variable: VariableDef = { type: type ?? 'text' };
    if (typeof spec['unit'] === 'string') variable.unit = spec['unit'];
    if (typeof spec['source'] === 'string') variable.source = spec['source'];
    out[name] = variable;
  }
  return out;
}

function normalizeSteps(raw: unknown, where: string, issues: Issue[]): Step[] {
  if (!Array.isArray(raw)) {
    issues.push(issue('error', 'bad_structure', `${where} must have a list of steps.`));
    return [];
  }
  const steps: Step[] = [];
  for (const item of raw) {
    if (!isRecord(item) || typeof item['id'] !== 'string' || typeof item['type'] !== 'string') {
      issues.push(issue('error', 'bad_structure', `Every step in ${where} needs an id and a type.`));
      continue;
    }
    const step: Step = { ...(item as Step) };
    if ('steps' in item) {
      if (Array.isArray(item['steps'])) {
        step.steps = normalizeSteps(item['steps'], `step "${item['id']}"`, issues);
      } else {
        delete step.steps;
        issues.push(issue('error', 'bad_structure', `The steps of "${item['id']}" must be a list.`, item['id']));
      }
    }
    steps.push(step);
  }
  return steps;
}

export function normalizeWorkflow(raw: unknown): { workflow: Workflow; issues: Issue[] } {
  const issues: Issue[] = [];
  if (!isRecord(raw)) {
    issues.push(issue('error', 'bad_structure', 'The workflow block must be a mapping with sections or steps.'));
    return { workflow: { variables: {}, sections: [] }, issues };
  }
  warnUnknownKeys(raw, WORKFLOW_KEYS, 'The workflow', issues);
  const variables = normalizeVariables(raw['variables'], issues);
  if (raw['sections'] !== undefined && raw['steps'] !== undefined) {
    issues.push(
      issue(
        'error',
        'bad_structure',
        'Use sections or steps at the top of the workflow, not both. The top level steps are dropped.'
      )
    );
  }
  let sections: Section[] = [];
  if (raw['sections'] !== undefined) {
    if (!Array.isArray(raw['sections'])) {
      issues.push(issue('error', 'bad_structure', 'sections must be a list.'));
    } else {
      for (const item of raw['sections']) {
        if (!isRecord(item) || typeof item['id'] !== 'string') {
          issues.push(issue('error', 'bad_structure', 'Every section needs an id.'));
          continue;
        }
        warnUnknownKeys(item, SECTION_KEYS, `Section "${item['id']}"`, issues);
        const title = typeof item['title'] === 'string' ? item['title'] : item['id'];
        sections.push({
          id: item['id'],
          title,
          steps: normalizeSteps(item['steps'] ?? [], `section "${item['id']}"`, issues),
        });
      }
    }
  } else if (raw['steps'] !== undefined) {
    sections = [{ id: 'main', title: 'Steps', steps: normalizeSteps(raw['steps'], 'the workflow', issues) }];
  }
  return { workflow: { variables, sections }, issues };
}
